import { IN_TO_M, SPECIAL_OVERLAY_CHECK } from '../constants';
import message from '../helper/message';

/**
 * this file contains all the method related to configurator
 * all the public api should take metadata as the first argument
 * and return the method function
 */

/**
 * public api
 * init the default configuration
 */
const initConfiguration = (metadata) => () =>
  Object.keys(metadata.tabs).reduce(
    (config, tabName) =>
      Object.assign(config, initTabAttribute(metadata, tabName)),
    {}
  );

function initTabAttribute(metadata, tab) {
  const attributes = metadata.tabs[tab].attributeGroups.reduce(
    (attributes, groupName) =>
      attributes.concat(
        metadata.attributeGroups[groupName].attributes.map(
          (attrName) => metadata.attributes[attrName]
        )
      ),
    []
  );
  return attributes.reduce(
    (config, attribute) => Object.assign(config, initAttribute(attribute)),
    {}
  );
}

function initAttribute(attribute) {
  switch (attribute.type) {
    case 'list':
      const lists = (attribute.products || []).reduce((lists, product) => {
        lists[product] = [];
        return lists;
      }, {});
      return { [attribute.name]: lists };
    default:
      return { [attribute.name]: {} };
  }
}

/**
 * public api
 * Given current configuration and an array of changes, return the new configuration
 */

const setConfiguration = (metadata, onConfigChange) => (
  configuration,
  models,
  changes
) => {
  return changes.reduce(
    (config, change) => {
      onConfigChange && onConfigChange(change);
      return Object.assign(
        config,
        setConfigurationAttribute(metadata, config, models, change)
      );
    },
    { ...configuration }
  );
};

function setConfigurationAttribute(
  metadata,
  configuration,
  models,
  { attribute, value, change }
) {
  const { attributes } = metadata;
  let newConfig = { ...configuration };

  function attributeRemove(modelId, attribute, value) {
    if (!modelId) return;
    if (value) {
      const items = newConfig[attribute][value];
      if (!items.length) return;
      const itemIndex = items.map((item) => item.modelId).indexOf(modelId);
      if (itemIndex === -1)
        return message.warn(
          `Product ${value} under attribute ${attribute} of configuration doesn't contains a model with modelId ${change}!`
        );
      newConfig[attribute][value].splice(itemIndex, 1);
    } else {
      Object.assign(newConfig, initAttribute(attributes[attribute]));
    }
    models[modelId].children
      .map((childId) => ({ ...models[childId], modelId: childId }))
      .forEach((child) =>
        attributeRemove(
          child.modelId,
          child.attribute,
          newConfig[child.attribute][child.value] && child.value
        )
      );
  }
  if (change !== undefined) {
    newConfig[attribute] = {
      ...configuration[attribute],
      [value]: configuration[attribute][value].slice(),
    };
    if (change === -1 || typeof change === 'string') {
      // remove item
      const multiAttributeModelId =
        change === -1
          ? newConfig[attribute][value][newConfig[attribute][value].length - 1]
              .modelId
          : change;
      attributeRemove(multiAttributeModelId, attribute, value);
    } else if (typeof change === 'object') {
      // add item
      newConfig[attribute][value].push(change);
    } else message.warn('Unknow change for setConfiguration');
  } else {
    const singleAttributeModelId = configuration[attribute].modelId;
    singleAttributeModelId &&
      attributeRemove(singleAttributeModelId, attribute);
    newConfig[attribute] = value || {};
  }
  return newConfig;
}

/**
 * public api
 */
const getTab = (metadata) => {
  const allTabs = Object.values(metadata.tabs);
  return (tabName) => (tabName ? metadata.tabs[tabName] : allTabs);
};

/**
 * public api
 */
const getAttributeGroup = (metadata) => (groupName) =>
  metadata.attributeGroups[groupName];

/**
 * public api
 */
const getAttribute = (metadata) => (attributeName) =>
  metadata.attributes[attributeName];

/**
 * public api
 */
const getProduct = (metadata) => (productName) =>
  metadata.products[productName];

/**
 * public api
 * get standard attribute available values based on dependents
 */
const getAttributeAvailableValues = (metadata) => (
  attributeName,
  configuration,
  optionValue
) => {
  const { attributes, products } = metadata;
  const {
    hasScene,
    dependOn,
    autoRemoveInvalid,
    filter,
    filterOption,
  } = attributes[attributeName];
  // special case
  if (filter && configuration[filter].value !== filterOption) return [];

  let allProducts = attributes[attributeName].products;
  if (dependOn)
    allProducts = allProducts.filter((product) => {
      for (let idx = 0; idx < dependOn.length; ++idx) {
        const dependOnValueCompatibility = getAttributeValueCompatibility(
          metadata,
          dependOn[idx],
          configuration,
          optionValue
        );
        if (!dependOnValueCompatibility || !dependOnValueCompatibility[product])
          return false;
      }
      return true;
    });

  allProducts = allProducts.map((product) => ({
    value: product,
    text: products[product].label,
    disabled: hasScene && (!products[product].sceneId && product !== 'None'),
    thumbnailurl: products[product].thumbnailUrl,
    invalidtext: products[product].invalidText,
  }));

  if (autoRemoveInvalid)
    allProducts = allProducts.filter((product) => !product.disabled);

  if (allProducts.length === 1 && allProducts[0].value === 'None') return [];

  return allProducts;
};

/**
 * public api
 * get color available values based on selected model
 */
const getAvailableColors = (metadata) => (attributeName, selectedProduct) => {
  const { attributes, products, compatibility } = metadata;
  const { hasScene, autoRemoveInvalid } = attributes[attributeName];

  let allProducts = attributes[attributeName].products;

  allProducts = allProducts.filter((product) => {
    const dependOnValueCompatibility = compatibility[selectedProduct];
    if (!dependOnValueCompatibility || !dependOnValueCompatibility[product])
      return false;
    return true;
  });

  allProducts = allProducts.map((product) => ({
    value: product,
    text: products[product].label,
    disabled: hasScene && (!products[product].sceneId && product !== 'None'),
    thumbnailurl: products[product].thumbnailUrl,
  }));

  if (autoRemoveInvalid)
    allProducts = allProducts.filter((product) => !product.disabled);

  if (allProducts.length === 1 && allProducts[0].value === 'None') return [];

  return allProducts;
};

function getAttributeValueCompatibility(
  metadata,
  attributeName,
  configuration,
  optionValue
) {
  const { bases, compatibility } = metadata;
  if (optionValue) return compatibility[optionValue];
  switch (attributeName) {
    case 'base':
      const { base, depth, width } = configuration;
      if (!(base.value && depth.value && width.value)) return;
      return compatibility[bases[base.value][depth.value][width.value]];
    default:
      return compatibility[configuration[attributeName].value];
  }
}

/**
 * public api
 * the base is a non-standard attribute and needs a unique approach
 */
const getAvailableBase = (metadata) => () => {
  const { attributes, bases } = metadata;
  const { autoRemoveInvalid } = attributes.base;
  let availableBase = Object.keys(bases).map((base) => ({
    value: base,
    text: base,
    disabled: !isBaseAvailable(metadata, bases[base]),
  }));
  if (autoRemoveInvalid)
    availableBase = availableBase.filter((product) => !product.disabled);

  return availableBase;
};

function isBaseAvailable(metadata, baseInfo) {
  const depthes = Object.keys(baseInfo);
  for (let idx = 0; idx < depthes.length; ++idx)
    if (isDepthAvailable(metadata, baseInfo[depthes[idx]])) return true;

  return false;
}

/**
 * public api
 * the depth is a non-standard attribute and needs a unique approach
 */
const getAvailableDepth = (metadata) => (configuration) => {
  const { bases, attributes } = metadata;
  const selectedBase = configuration.base.value;
  const { autoRemoveInvalid } = attributes.depth;
  let availableDepth = (selectedBase
    ? Object.keys(bases[selectedBase])
    : []
  ).map((depth) => ({
    value: Number(depth),
    text: depth,
    disabled: !isDepthAvailable(metadata, bases[selectedBase][depth]),
  }));
  if (autoRemoveInvalid)
    availableDepth = availableDepth.filter((product) => !product.disabled);

  return availableDepth;
};

function isDepthAvailable(metadata, depthInfo) {
  const widthes = Object.keys(depthInfo);
  for (let idx = 0; idx < widthes.length; ++idx)
    if (isWidthAvailable(metadata, depthInfo[widthes[idx]])) return true;

  return false;
}

/**
 * public api
 * the width is a non-standard attribute and needs a unqiue approach
 */
const getAvailableWidth = (metadata) => (configuration) => {
  const { bases, attributes } = metadata;
  const selectedBase = configuration.base.value;
  const selectedDepth = configuration.depth.value;
  const { autoRemoveInvalid } = attributes.width;

  let availableWidth = (selectedDepth
    ? Object.keys(bases[selectedBase][selectedDepth])
    : []
  ).map((width) => ({
    text: width,
    value: Number(width),
    disabled: !isWidthAvailable(
      metadata,
      bases[selectedBase][selectedDepth][width]
    ),
  }));
  if (autoRemoveInvalid)
    availableWidth = availableWidth.filter((product) => !product.disabled);

  return availableWidth;
};

function isWidthAvailable(metadata, baseName) {
  return !!metadata.products[baseName].sceneId;
}

/**
 * public api
 * get default base product for init before user specific all paramater
 * default depth will be 30 and default width will be 60
 */
const getDefaultBaseProduct = (metadata) => (
  base,
  optionDepth,
  options = {}
) => {
  const bases = metadata.bases[base];
  if (!bases) return;
  let { width, depth } = options;
  width = width || 60;
  depth = optionDepth || depth || 30;
  if (!bases[depth]) {
    let offset = 1;
    while (offset) {
      if (bases[depth - offset]) {
        depth -= offset;
        break;
      }
      if (bases[depth + offset]) {
        depth += offset;
        break;
      }
      ++offset;
    }
  }
  if (!bases[depth][width]) {
    let offset = 1;
    while (offset) {
      if (bases[depth][width - offset]) {
        width -= offset;
        break;
      }
      if (bases[depth][width + offset]) {
        width += offset;
        break;
      }
      ++offset;
    }
  }
  return { depth, width };
};

/**
 * public api
 * given current configuration, return the product name of selected base
 */
const getBaseProduct = (metadata) => (base, depth, width) => {
  const { bases, products } = metadata;
  return products[bases[base][depth][width]];
};

/**
 * public api
 * get the blocks data of given product
 */
const getProductBlockData = (metadata) => (attributeName, productName) => {
  const { attributes, products } = metadata;
  const attribute = attributes[attributeName];
  if (!attribute.blocks) return;
  const product = products[productName];
  return Object.keys(attribute.blocks).reduce((blockData, blockName) => {
    const block = attribute.blocks[blockName];
    const width = parseDimension(product, block.width);
    const height = parseDimension(product, block.height);
    const rowHeight =
      typeof block.rowHeight === 'string'
        ? product[block.rowHeight]
        : block.rowHeight;
    const colWidth =
      typeof block.colWidth === 'string'
        ? product[block.colWidth]
        : block.colWidth;
    let rowNum = Math.floor(height / rowHeight);
    let colNum = Math.floor(width / colWidth);

    const rowHeights = Array(rowNum).fill(rowHeight * IN_TO_M);
    const colWidths = Array(colNum).fill(colWidth * IN_TO_M);

    const currentHeight = rowNum * rowHeight;
    const currentWidth = colNum * colWidth;
    if (currentHeight < height) {
      rowHeights.push((height - currentHeight) * IN_TO_M);
      rowNum += 1;
    }
    if (currentWidth < width) {
      colWidths.push((width - currentWidth) * IN_TO_M);
      colNum += 1;
    }

    if (block.additionRow) {
      rowHeights[rowNum - 1] -= block.additionRow * IN_TO_M;
      rowHeights[rowNum] = block.additionRow * IN_TO_M;
    }

    if (block.additionCol) {
      colWidths[colNum - 1] -= block.additionCol * IN_TO_M;
      colWidths[colNum] = block.additionCol * IN_TO_M;
    }

    if (block.extraRow) rowHeights.push(block.extraRow * IN_TO_M);

    if (block.extraCol) colWidths.push(block.extraCol * IN_TO_M);
    const margins = [
      'marginTop',
      'marginBottom',
      'marginLeft',
      'marginRight',
    ].reduce((acc, margin) => {
      acc[margin] = parseDimension(product, block[margin], true);
      if (!acc[margin]) return acc;
      acc[margin] *= IN_TO_M;
      return acc;
    }, {});
    blockData[blockName] = {
      rowHeights,
      colWidths,
      withRotation: !!block.withRotation,
      occupyRow: parseOccupy(product, block.occupyRow, rowHeights.length),
      occupyCol: parseOccupy(product, block.occupyCol, colWidths.length),
      ...margins,
    };
    return blockData;
  }, {});
};

function parseDimension(product, text, suppressWarning = false) {
  if (text === undefined) {
    if (!suppressWarning)
      message.warn('ParseDimension missing expression for parsing!');
    return;
  }
  const textAsNum = Number(text);
  if (!Number.isNaN(textAsNum)) return textAsNum; // if the dimension is value directly, like 30
  if (product.hasOwnProperty(text) && !Number.isNaN(product[text]))
    return product[text]; // if the dimension is a string that refers to the product data, like width
  if (text.startsWith('calc')) return parseCalcExpression(product, text);
  if (text.startsWith('max')) return parseMaxExpression(product, text);
  if (text.startsWith('min')) return parseMinExpression(product, text);
  if (text.startsWith('switch')) return parseSwitchExpression(product, text);

  if (
    suppressWarning &&
    product.hasOwnProperty(text) &&
    product[text] === undefined
  )
    return;

  throw new Error(
    `Unknown block dimension expression ${text} for product ${product}`
  );
}

function parseOccupy(product, text, length) {
  if (!text) return;
  const occupy = Array(length).fill(false);
  function occupyEntry(idx) {
    if (idx < 0 || idx >= occupy.length)
      return message.warn(
        `Invalid occpuy index ${idx} for product ${product.name}!`
      );
    occupy[idx] = true;
  }

  const segments = parseSpecialExpressionText(text).filter(
    (segment) => !!segment
  );

  segments.forEach((segment) => {
    const segmentAsNum = Number(segment);
    if (Number.isNaN(segmentAsNum)) {
      const [begin, end] = segment
        .split('~')
        .map((subSegment) => parseDimension(product, subSegment.trim()));
      for (let idx = begin; idx < end; ++idx) occupyEntry(idx);
    } else occupyEntry(segmentAsNum);
  });
  return occupy;
}

function parseCalcExpression(product, text) {
  const subText = text.substring(text.indexOf('(') + 1, text.lastIndexOf(')'));
  const [firstText, operator, secondText] = parseSpecialExpressionText(subText);
  const firstValue = parseDimension(product, firstText);
  const secondValue = parseDimension(product, secondText);
  switch (operator) {
    case '+':
      return firstValue + secondValue;
    case '-':
      return firstValue - secondValue;
    case '*':
      return firstValue * secondValue;
    case '/':
      return firstValue / secondValue;
    default:
      throw new Error(`Invalid operator in ${subText}`);
  }
}

function parseMaxExpression(product, text) {
  const subText = text.substring(text.indexOf('(') + 1, text.lastIndexOf(')'));
  const [firstText, secondText] = parseSpecialExpressionText(subText);
  const firstValue = parseDimension(product, firstText);
  const secondValue = parseDimension(product, secondText);
  return Math.max(firstValue, secondValue);
}

function parseMinExpression(product, text) {
  const subText = text.substring(text.indexOf('(') + 1, text.lastIndexOf(')'));
  const [firstText, secondText] = parseSpecialExpressionText(subText);
  const firstValue = parseDimension(product, firstText);
  const secondValue = parseDimension(product, secondText);
  return Math.min(firstValue, secondValue);
}

function parseSwitchExpression(product, text) {
  const subText = text.substring(text.indexOf('(') + 1, text.lastIndexOf(')'));
  const switchTexts = parseSpecialExpressionText(subText);
  const switchKey = switchTexts[0];
  if (!switchKey)
    throw new Error(`Switch expression '${text}' missing key attribute!`);

  const productValue = product[switchKey];
  if (!productValue)
    throw new Error(
      `Switch expression '${text}' with unknow key '${switchKey}' for product '${
        product.name
      }'`
    );
  if (switchTexts.length < 2)
    throw new Error(`Switch expression '${text}' missing cases!`);
  const caseMap = {};
  for (let idx = 1; idx < switchTexts.length; ++idx) {
    const caseTexts = switchTexts[idx].split(':');
    if (caseTexts.length !== 2)
      throw new Error(`Invalid switch case '${switchTexts[idx]}'`);
    caseMap[caseTexts[0]] = caseTexts[1];
  }
  const dimensionText = caseMap[productValue] || caseMap.default;
  if (!dimensionText)
    throw new Error(
      `Unknow case '${productValue}' for switch expression '${text}', please either add the case or add a default case!`
    );
  return parseDimension(product, dimensionText);
}

function parseSpecialExpressionText(text) {
  let openingBracket = 0;
  let beginIdx = 0;
  const result = [];
  for (let idx = 0; idx < text.length; ++idx) {
    const char = text[idx];
    if (char === '(') ++openingBracket;
    if (char === ')') --openingBracket;
    if (!openingBracket && char === ',') {
      result.push(text.substring(beginIdx, idx).trim());
      beginIdx = idx + 1;
    }
  }
  result.push(text.substring(beginIdx).trim());
  return result;
}

/**
 * public api
 * Can only get the dependOn Blocks if the part that this attribute depend on is belong a single value attribute
 * otherwise an error will the thrown
 */
const getAttributeDependOnBlocks = (metadata) => (attribute, configuration) => {
  const { attributes } = metadata;
  const { blockDependOn } = attributes[attribute];
  if (!blockDependOn) return;
  return blockDependOn
    .filter(([dependAttr]) => !!configuration[dependAttr].blocks)
    .map(
      ([dependAttr, blockName]) => configuration[dependAttr].blocks[blockName]
    );
};

const getModelDependOnBlocks = (metadata) => {
  function getDependOnBlocks(
    modelId,
    configuration,
    models,
    dependOnAttr,
    options = {}
  ) {
    const { blockName, dependOnValue } = options;
    const { attributes, compatibility } = metadata;
    const { value: modelValue } = models[modelId];
    const attributeType = attributes[dependOnAttr].type;
    if (attributeType === 'list') {
      const items = dependOnValue
        ? configuration[dependOnAttr][dependOnValue]
        : Object.keys(configuration[dependOnAttr])
            .filter(
              (attributeValue) => compatibility[attributeValue][modelValue]
            )
            .reduce(
              (items, attributeValue) =>
                items.concat(configuration[dependOnAttr][attributeValue]),
              []
            );
      const blocks = items.reduce((blocks, item) => {
        if (!item.blocks) return blocks;
        return item.blocks[blockName]
          ? blocks.concat(item.blocks[blockName])
          : blocks.concat(Object.values(item.blocks));
      }, []);

      return blocks.find((block) => block.containNode(modelId));
    }
    const blocks = configuration[dependOnAttr].blocks;
    if (!blocks) return;

    if (blockName)
      return (
        blocks[blockName] &&
        blocks[blockName].containNode(modelId) &&
        blocks[blockName]
      );
    return Object.values(blocks).find((block) => block.containNode(modelId));
  }

  return (modelId, configuration, models) => {
    if (!models[modelId])
      throw new Error(
        `Can not get model depend on block, model ${modelId} does not exist`
      );
    const { attributes } = metadata;
    const { parent, attribute } = models[modelId];
    const { blockDependOn, secondaryBlockDependOn } = attributes[attribute];
    const { attribute: parentAttribute, value: parentValue } = models[parent];

    let dependOnBlock, secondaryDependOnBlocks;
    for (let idx = 0; idx < blockDependOn.length && !dependOnBlock; ++idx) {
      const depend = blockDependOn[idx];
      if (depend[0] !== parentAttribute) continue;
      dependOnBlock = getDependOnBlocks(
        modelId,
        configuration,
        models,
        parentAttribute,
        { dependOnValue: parentValue, blockName: depend[1] }
      );
      if (!dependOnBlock) continue;
      secondaryDependOnBlocks =
        secondaryBlockDependOn &&
        secondaryBlockDependOn.map(([dependOnAttr, blockName]) =>
          getDependOnBlocks(modelId, configuration, models, dependOnAttr, {
            blockName,
          })
        );
    }
    if (!dependOnBlock)
      throw new Error(
        'Can not get Model depend on black, parent item does not match block depend on data!'
      );

    return { dependOnBlock, secondaryDependOnBlocks };
  };
};

const getSpecialOverlayValidationData = () => (
  modelName,
  configuration,
  models
) => {
  let overlayCheckIdx,
    incompatibleItemIdx,
    match = false;
  for (
    overlayCheckIdx = 0;
    overlayCheckIdx < SPECIAL_OVERLAY_CHECK.length;
    ++overlayCheckIdx
  ) {
    const incompatibleItems =
      SPECIAL_OVERLAY_CHECK[overlayCheckIdx].incompatibleItems;
    for (
      incompatibleItemIdx = 0;
      incompatibleItemIdx < incompatibleItems.length;
      ++incompatibleItemIdx
    ) {
      if (
        incompatibleItems[incompatibleItemIdx].items.indexOf(modelName) !== -1
      ) {
        match = true;
        break;
      }
    }
    if (match) break;
  }

  if (overlayCheckIdx === SPECIAL_OVERLAY_CHECK.length) return;

  const incompatibleMetadata = [];
  for (
    let idx = 0;
    idx < SPECIAL_OVERLAY_CHECK[overlayCheckIdx].incompatibleItems.length;
    ++idx
  ) {
    if (idx === incompatibleItemIdx) continue;
    const { items, block } = SPECIAL_OVERLAY_CHECK[
      overlayCheckIdx
    ].incompatibleItems[idx];
    const itemBlock = configuration[block[0]].blocks[block[1]];
    const itemsToCheck = Object.keys(itemBlock.models).filter((modelId) =>
      items.includes(models[modelId].value)
    );
    if (itemsToCheck.length)
      incompatibleMetadata.push({
        block: itemBlock,
        items: itemsToCheck,
        axis: SPECIAL_OVERLAY_CHECK[overlayCheckIdx].axis,
      });
  }
  return incompatibleMetadata.length ? incompatibleMetadata : undefined;
};

const configApiCreater = (metadata, onConfigChange) => ({
  initConfiguration: initConfiguration(metadata),
  getAttributeAvailableValues: getAttributeAvailableValues(metadata),
  getAvailableBase: getAvailableBase(metadata),
  getAvailableDepth: getAvailableDepth(metadata),
  getAvailableWidth: getAvailableWidth(metadata),
  getDefaultBaseProduct: getDefaultBaseProduct(metadata),
  getBaseProduct: getBaseProduct(metadata),
  setConfiguration: setConfiguration(metadata, onConfigChange),
  getTab: getTab(metadata),
  getAttributeGroup: getAttributeGroup(metadata),
  getAttribute: getAttribute(metadata),
  getProduct: getProduct(metadata),
  getProductBlockData: getProductBlockData(metadata),
  getAttributeDependOnBlocks: getAttributeDependOnBlocks(metadata),
  getModelDependOnBlocks: getModelDependOnBlocks(metadata),
  getAvailableColors: getAvailableColors(metadata),
  getSpecialOverlayValidationData: getSpecialOverlayValidationData(),
});

export default configApiCreater;



// WEBPACK FOOTER //
// ./src/modules/config.js